import { WipayPaymentMethod } from '@payment-terminals/interfaces/wipay';
import { Payment } from '@accounting/interfaces/payment';
import { WipayPaymentStatus, WipayResponseData } from '@accounting/interfaces/wipay';
import WipayTicket from '@payment-terminals/components/wipay-ticket/wipay-ticket.vue';
import { Terminal } from '@payment-terminals/interfaces/terminal';
import { WipayTicketPrintArray } from '@payment-terminals/interfaces/wipay';
import moment from 'moment';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component({
  name: 'bpc-wipay-terminal-payment-modal',
  components: {
    'bpc-wipay-ticket': WipayTicket,
  },
})
export default class WipayTerminalPaymentModal extends Vue {
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop({ type: Boolean, default: true }) readonly displayTicketToPrint: boolean;
  @Prop({ type: String, default: 'payment_terminals.modal.pay_with_wipay' })
  readonly modalTitle: string;
  @Prop({ type: Number, default: 0 }) readonly payAmount: number;
  @Prop(Number) readonly invoiceId: number;
  @Prop(Number) readonly contactId: number;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly terminals: Terminal[];
  @Prop({
    type: String,
    default: 'debit',
  })
  readonly wipayPaymentMethod: WipayPaymentMethod;

  currentPayment: Payment = null;
  key: number = 0;
  blockBtnPay: boolean = false;
  blockBtnClose: boolean = false;
  loadingPayment: boolean = false;
  showAlert: boolean = false;
  alertVariant: string = 'null';
  alertMessage: string = '';
  paymentSuccess: boolean = false;
  paymentData: object = null;

  savingPayment: boolean = false;

  selectedTerminalId: number = null;

  payment_ssai: number = null;

  showTechnicalError = false;
  technicalErrorMessage = 'payment_terminals.modal.payment_intent_sent_to_terminal_technical_error';

  paymentStatusAlertVariant: string = 'null';
  showPaymentStatusAlert: boolean = false;
  paymentStatusAlertMessage: string = '';
  paymentStatusResult: WipayPaymentStatus = null;
  showSendToTerminalButton: boolean = true;
  showUpdatePaymentStatusButton: boolean = false;

  wipayTicket: WipayTicketPrintArray = [];
  showWipayTicket: boolean = false;

  public successText: string = 'global.generic.successful_action';
  public errorText: string = 'global.generic.error_form_action';
  refreshInterval = null;

  get wipayActivated() {
    // return this.$store.getters['centerSettings/wipayActivated'];
    return true;
  }

  get availableTerminals() {
    return this.terminals.map(terminal => {
      return {
        text: `[${terminal.device_id}] ${terminal.name}`,
        value: terminal.id,
      };
    });
  }

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  get paymentId(): number {
    return this.currentPayment?.id || null;
  }

  async mounted() {
    this.showSendToTerminalButton = true;
    if (!this.wipayActivated) {
      this.showAlertMessage('danger', 'payment_terminals.modal.wipay_is_not_active');
    } else if (!this.isValidPayAmount()) {
      this.blockBtnPay = true;
      this.showAlertMessage('danger', 'payment_terminals.modal.not_enough_amount');
    }

    if (this.terminals.length) {
      this.selectedTerminalId = this.terminals[0]?.id;
    }
    this.listenToWebsocketEvents();

    if (this.terminals.length === 1) {
      await this.makePayment();
    }
  }

  printTicket() {
    this.$emit('on-print-ticket');
    this.close();
  }

  destroyed() {
    this.$bpcEcho.leaveTerminalPaymentChannel();
    clearInterval(this.refreshInterval);
  }

  listenToWebsocketEvents() {
    this.$bpcEcho
      .getTerminalPaymentChannel()
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentCancelled', data => {
        // console.log('getTerminalPaymentChannel listen event', data);
        if (data.paymentId === this.paymentId) {
          this.updateModalWithTheLastPaymentDetails();
        }
      })
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentConfirmed', data => {
        // console.log('getTerminalPaymentChannel listen event', data);
        if (data.paymentId === this.paymentId) {
          this.updateModalWithTheLastPaymentDetails();
        }
      })
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentBusy', data => {
        // console.log('getTerminalPaymentChannel listen event', data);
        if (data.paymentId === this.paymentId) {
          this.updateModalWithTheLastPaymentDetails();
        }
      });
  }

  updateModalWithTheLastPaymentDetails() {
    // get the payment from backend
    const paymentDetailRoute = `${this.$bpcApiRouteHelper.getEntityPath('payments')}/${
      this.paymentId
    }`;

    this.$bpcApi.get(paymentDetailRoute).subscribe(res => {
      const paymentMethodResult = res.data?.payment_method_result;
      if (paymentMethodResult && paymentMethodResult.callback) {
        this.managePaymentStatusResponse(paymentMethodResult.callback);
      }
    });
  }

  changeSelectedTerminalId(terminal: Terminal) {
    this.selectedTerminalId = terminal.id;
  }

  async makePayment() {
    this.showSendToTerminalButton = false;
    this.showUpdatePaymentStatusButton = false;
    this.wipayTicket = null;
    this.showWipayTicket = false;
    // this.disableButtons(true);
    this.loadingPayment = true;
    this.payment_ssai = null;
    this.hideAlertMessage();
    this.hidePaymentStatusAlertMessage();
    this.showTechnicalErrorMessage(false);

    try {
      this.savingPayment = true;
      const saveEntityPath = `${this.$bpcApiRouteHelper.getApiPath('pos/payments')}`;
      const data = {
        invoice_id: this.invoiceId,
        paid_amount: this.payAmount,
        payment_method_type: this.wipayPaymentMethod ? this.wipayPaymentMethod : 'wipay_terminal',
        contact_id: this.contactId,
        terminal_id: this.selectedTerminalId,
      };
      const res = await this.$bpcApi.post(saveEntityPath, data).toPromise();
      const payment = res.data;
      this.currentPayment = payment;
      this.$emit('payment-created', payment);

      this.managePaymentIntentResponse(payment?.payment_method_result?.response);
    } catch (error) {
      this.$bpcMessage.showErrorMessage({ text: this.errorText });
      this.showSendToTerminalButton = true;
      this.showTechnicalErrorMessage();
    }
  }

  showTechnicalErrorMessage(show: boolean = true) {
    this.wipayTicket = null;
    this.showWipayTicket = false;
    this.showTechnicalError = show;
  }

  managePaymentIntentResponse(response: WipayResponseData) {
    if (!response.error) {
      // TODO: listen to the websocket
      this.blockBtnClose = false;
      this.loadingPayment = false;
      this.payment_ssai = response.ssai;
      this.showUpdatePaymentStatusButton = true;

      this.showPaymentStatusAlertMessage(
        'success',
        'payment_terminals.modal.payment_intent_sent_to_terminal_success',
      );

      this.updateStatusAndTryAgainAfterTimeout();
    } else {
      this.managePaymentIntentErrorResponse(response);
    }
  }

  managePaymentStatusResponse(response: WipayPaymentStatus) {
    this.paymentStatusResult = response;
    if (response.status === 'busy') {
      this.showUpdatePaymentStatusButton = true;
      this.wipayTicket = null;
      this.showWipayTicket = false;
      this.showPaymentStatusAlertMessage(
        'info',
        'payment_terminals.modal.payment_status_still_busy',
      );
    } else if (response.status === 'final' && response.approved) {
      this.showSendToTerminalButton = false;
      this.showUpdatePaymentStatusButton = false;
      this.wipayTicket = response.ticket;
      this.showWipayTicket = true;
      this.showPaymentStatusAlertMessage(
        'success',
        'payment_terminals.modal.payment_status_approved',
      );
      this.$emit('on-success-payment', this.paymentData);
    } else if (response.status === 'final' && response.cancelled) {
      this.showSendToTerminalButton = true;
      this.showUpdatePaymentStatusButton = false;
      this.wipayTicket = null;
      this.showWipayTicket = false;
      this.showPaymentStatusAlertMessage(
        'warning',
        'payment_terminals.modal.payment_status_cancelled',
      );
    } else {
      this.showTechnicalErrorMessage();
    }
  }

  managePaymentIntentErrorResponse(response: WipayResponseData) {
    this.key++;
    this.showSendToTerminalButton = true;
    this.showAlertMessage('danger', response.errormsg);
    this.disableButtons(false);
    this.loadingPayment = false;
  }

  async updatePaymentStatus() {
    return new Promise((resolve, reject) => {
      const paymentIntentRoute = `${this.$bpcApiRouteHelper.getEntityPath(
        'payments',
      )}/terminals/wipay/status`;
      this.paymentStatusResult = null;
      this.$bpcApi
        .put(paymentIntentRoute, {
          ssai: this.payment_ssai,
          terminalId: this.selectedTerminalId,
        })
        .subscribe(
          res => {
            resolve(res);
            // this.managePaymentStatusResponse(res.data);
          },
          error => {
            console.log(error);
            this.showTechnicalErrorMessage();
            reject(error);
          },
        );
    });
  }

  disableButtons(value) {
    this.blockBtnPay = value;
    this.blockBtnClose = value;
  }

  showAlertMessage(variant, message) {
    this.alertVariant = variant;
    this.alertMessage = message;
    this.showAlert = true;
  }

  hideAlertMessage() {
    this.alertVariant = 'null';
    this.alertMessage = '';
    this.showAlert = false;
  }

  showPaymentStatusAlertMessage(variant, message) {
    this.paymentStatusAlertVariant = variant;
    this.paymentStatusAlertMessage = message;
    this.showPaymentStatusAlert = true;
  }

  hidePaymentStatusAlertMessage() {
    this.paymentStatusAlertVariant = 'null';
    this.paymentStatusAlertMessage = '';
    this.showPaymentStatusAlert = false;
  }

  close() {
    this.hideAlertMessage();
    this.hidePaymentStatusAlertMessage();
    this.$emit('click-on-close');
  }

  isValidPayAmount() {
    return !(isNaN(this.payAmount) || this.payAmount <= 0);
  }

  async updateStatusAndTryAgainAfterTimeout() {
    this.refreshInterval = setInterval(async () => {
      if (this.showModal && this.showUpdatePaymentStatusButton) {
        await this.updatePaymentStatus();
        // await this.updateStatusAndTryAgainAfterTimeout();
      } else {
        clearInterval(this.refreshInterval);
      }
    }, 8000);
  }
}
